import { http } from '../client';
import type { CallOptions, ItemList, WriteOptions } from '../types';
import type {
  EppConnectionList,
  EppSessions,
  PollMessageDetail,
  PollMessageList,
  ReconciliationIssueDetail,
  ReconciliationQueue,
  RegistryApi,
  RegistryDetail,
  RegistrySummary,
  RegistryTransactionList,
  TldDetail,
  TldList,
  WorkflowDetail,
  WorkflowList,
} from './registry';

const seg = encodeURIComponent;
const rd = (o?: CallOptions) => ({ signal: o?.signal });
const wr = (o?: WriteOptions) => ({ signal: o?.signal, idempotencyKey: o?.idempotencyKey, notifyCustomer: o?.notifyCustomer });

export const registryHttp: RegistryApi = {
  getTldConnections: (ids, opts) => http.get('api', '/registry/tld-connections', { query: { tld_ids: ids.join(',') }, ...rd(opts) }),
  listTldCatalog: (q, opts) =>
    http.get('api', '/registry/tld-catalog', { query: { q: q.q, class: q.class, state: q.state, limit: q.limit, offset: q.offset }, ...rd(opts) }),
  enableTld: (zone, body, opts) => http.post('api', `/registry/tld-catalog/${seg(zone)}/enable`, body, wr(opts)),
  disableTld: (zone, body, opts) => http.post('api', `/registry/tld-catalog/${seg(zone)}/disable`, body, wr(opts)),
  listRegistries: (opts) => http.get<ItemList<RegistrySummary>>('api', '/registry/registries', rd(opts)),
  getRegistry: (code, opts) => http.get<RegistryDetail>('api', `/registry/registries/${seg(code)}`, rd(opts)),
  listTlds: (q, opts) =>
    http.get<TldList>('api', '/registry/tlds', { query: { registry: q.registry, status: q.status, limit: q.limit, after: q.after }, ...rd(opts) }),
  getTld: (id, opts) => http.get<TldDetail>('api', `/registry/tlds/${seg(id)}`, rd(opts)),
  getTldReadiness: (id, opts) => http.get('api', `/registry/tlds/${seg(id)}/readiness`, rd(opts)),
  testTldCheck: (id, body, opts) => http.post('api', `/registry/tlds/${seg(id)}/test-check`, body, rd(opts)),
  setTldPublication: (id, body, opts) => http.post('api', `/registry/tlds/${seg(id)}/publication`, body, wr(opts)),
  getTldSetupOptions: (q, opts) => http.get('api', '/registry/tld-setup/options', { query: { connection_id: q.connection_id, q: q.q, limit: q.limit }, ...rd(opts) }),
  setupTld: (body, opts) => http.post('api', '/registry/tld-setup', body, wr(opts)),
  listEppConnections: (q, opts) => http.get<EppConnectionList>('api', '/registry/epp-connections', { query: { registry: q.registry }, ...rd(opts) }),
  getEppSessions: (q, opts) => http.get<EppSessions>('api', '/registry/sessions', { query: { registry: q.registry }, ...rd(opts) }),
  listRegistryTransactions: (q, opts) =>
    http.get<RegistryTransactionList>('api', '/registry/transactions', {
      query: {
        registry: q.registry,
        operation: q.operation,
        state: q.state,
        object_type: q.object_type,
        traffic_class: q.traffic_class,
        object_key: q.object_key,
        trid: q.trid,
        before: q.before,
        limit: q.limit,
      },
      ...rd(opts),
    }),
  listPollMessages: (q, opts) =>
    http.get<PollMessageList>('api', '/registry/poll-messages', {
      query: { registry: q.registry, status: q.status, before: q.before, limit: q.limit },
      ...rd(opts),
    }),
  getPollMessage: (id, opts) => http.get<PollMessageDetail>('api', `/registry/poll-messages/${seg(id)}`, rd(opts)),
  getReconciliationQueue: (q, opts) =>
    http.get<ReconciliationQueue>('api', '/registry/reconciliation', {
      query: { registry: q.registry, state: q.state, issue_type: q.issue_type, limit: q.limit },
      ...rd(opts),
    }),
  getReconciliationIssue: (id, opts) => http.get<ReconciliationIssueDetail>('api', `/registry/reconciliation/${seg(id)}`, rd(opts)),
  listWorkflows: (q, opts) =>
    http.get<WorkflowList>('api', '/workflows', {
      query: {
        state: q.state,
        workflow_type: q.workflow_type,
        resource_id: q.resource_id,
        correlation_id: q.correlation_id,
        before: q.before,
        limit: q.limit,
      },
      ...rd(opts),
    }),
  getWorkflow: (id, opts) => http.get<WorkflowDetail>('api', `/workflows/${seg(id)}`, rd(opts)),
};
